import { useEffect, useState, useMemo, useCallback } from "react";
import {
    Chip,
    Spinner,
    Tooltip,
} from "@heroui/react";
import {
    ArrowUpRight,
    Bank,
    Building,
    CurrencyEur,
    Flag,
    Pencil,
    Stack,
    UsersThree,
} from "@phosphor-icons/react";
import {
    FlagForWatchListModal,
    WatchListModalState,
} from "screens/buildings/components/rentalTab/modals/flagForWatchlist";
import {
    ContactType,
    MilestoneDateType,
    RentalContract,
} from "shared/types/rental-contracts";
import { formatDate } from "shared/utils/get-dates";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { EditWatchListModal } from "./edit-watchlist-modal";
import { Link } from "react-router-dom";
import { UniversalTable, UniversalTableSearch } from 'shared/components/UniversalTable';
import type { UniversalFilterSection, UniversalGroupByOption, CellConfig } from 'shared/components/UniversalTable';

// Column configuration for watchlist table
const WATCHLIST_COLUMNS = [
  { id: 'building', label: 'Gebouw', minWidth: 120, defaultWeight: 1, sortKey: 'building' },
  { id: 'unit', label: 'Unit', minWidth: 150, defaultWeight: 1, sortKey: 'unit' },
  { id: 'company', label: 'Vennootschap', minWidth: 160, defaultWeight: 2, sortKey: 'company' },
  { id: 'tenant', label: 'Huurder', minWidth: 200, defaultWeight: 2 },
  { id: 'reason', label: 'Reden op watchlist', minWidth: 250, defaultWeight: 3 },
  { id: 'startDate', label: 'Startdatum', minWidth: 120, defaultWeight: 1, sortKey: 'startDate' },
  { id: 'openstand', label: 'Openstand', minWidth: 130, defaultWeight: 1, sortKey: 'openstand' },
  { id: 'actions', label: 'Acties', minWidth: 100, defaultWeight: 0 }
];

const formatEuro = (amount: number): string =>
    new Intl.NumberFormat('nl-BE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);

export interface WatchListSectionProps {
}

const WatchListSection = ({
}: WatchListSectionProps) => {
    const [contracts, setContracts] = useState<RentalContract[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isFlagForWatchListModalOpen, setIsFlagForWatchListModalOpen] =
        useState<boolean>(false);
    const [selectedWatchlistContract, setSelectedWatchlistContract] =
        useState<any>(null);
    const [editWatchlitReasonModalOpen, setEditWatchlistReasonModalOpen] = useState<boolean>(false);
    const [selectedContract, setSelectedContract] = useState<RentalContract | null>(null);

    // Search and filter state
    const [searchText, setSearchText] = useState('');
    const [buildingFilter, setBuildingFilter] = useState<string[]>([]);
    const [unitFilter, setUnitFilter] = useState<string[]>([]);
    const [groupBy, setGroupBy] = useState('none');

    const { session } = useSession();

    const handleOpenEditWatchListReason = (contract: any) => {
        setSelectedContract(contract);
        setEditWatchlistReasonModalOpen(true);
    };

    const softUnflagFromWatchList = async (contractId: number) => {
        console.log("Soft unflagging from watchlist for contract: ", contractId);
        setContracts(contracts.filter(contract => contract.id !== contractId));
    };

    const handleClose = () => {
        setSelectedContract(null);
        setEditWatchlistReasonModalOpen(false);
    };

    const fetchWatchListContracts = async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/rental/watchlist`,
                { method: "GET" },
                session,
            );
            const data = await response.json();
            console.log(data);
            // Sort contracts by building ID
            const sortedData = data.sort((a: RentalContract, b: RentalContract) => {
                const buildingIdA = Number(a?.unit?.building?.id) || 0;
                const buildingIdB = Number(b?.unit?.building?.id) || 0;
                return buildingIdA - buildingIdB;
            });
            setContracts(sortedData);
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to fetch missing documents:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchWatchListContracts();
    }, []);

    const handleUnflagFlagFromWatchList = async (contract: any) => {
        console.log("Handling unflag from watchlist for contract: ", contract);
        setSelectedWatchlistContract(contract);
        setIsFlagForWatchListModalOpen(true);
    };
    const handleCloseFlagForWatchListModal = () => {
        setSelectedWatchlistContract(null);
        setIsFlagForWatchListModalOpen(false);
    };

    // Filter sections configuration
    const filterSections: UniversalFilterSection[] = useMemo(() => [
        {
            key: 'building',
            label: 'Gebouw',
            options: [],
            color: 'neutral' as const,
            extractOptionsFromData: (rows: RentalContract[]) => {
                const buildings = rows
                    .map(c => c.unit?.building?.id)
                    .filter(Boolean);
                return [...new Set(buildings)].map(id => ({ key: id!, label: id! }));
            }
        },
        {
            key: 'unit',
            label: 'Unit',
            options: [],
            color: 'neutral' as const,
            extractOptionsFromData: (rows: RentalContract[]) => {
                const units = rows.map(c => c.unit?.id).filter(Boolean);
                return [...new Set(units)].map(id => ({ key: id!, label: id! }));
            }
        }
    ], []);

    // Group by options
    const groupByOptions: UniversalGroupByOption[] = useMemo(() => [
        { key: 'none', label: 'Geen groepering' },
        { key: 'building', label: 'Per gebouw' }
    ], []);

    // Filtered contracts
    const filteredContracts = useMemo(() => {
        return contracts.filter(contract => {
            // Search filter
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const matchesSearch = (
                    contract.unit?.building?.id?.toLowerCase().includes(searchLower) ||
                    contract.unit?.id?.toLowerCase().includes(searchLower) ||
                    contract.flaggedForWatchlistReason?.toLowerCase().includes(searchLower) ||
                    contract.contractContacts?.some(c =>
                        c.resource?.name?.toLowerCase().includes(searchLower)
                    )
                );
                if (!matchesSearch) return false;
            }

            // Building filter
            if (buildingFilter.length && !buildingFilter.includes(contract.unit?.building?.id || '')) {
                return false;
            }

            // Unit filter
            if (unitFilter.length && !unitFilter.includes(contract.unit?.id || '')) {
                return false;
            }

            return true;
        });
    }, [contracts, searchText, buildingFilter, unitFilter]);

    // Render row callback
    const renderRow = useCallback((contract: RentalContract): CellConfig[] => {
        const tenants = contract.contractContacts
            ?.filter(c => c.contactType === ContactType.TENANT && c.resource?.name)
            .map(c => c.resource);

        const startDate = contract.contractMilestoneDates
            ?.find(m => m.milestoneDateType === MilestoneDateType.START_DATE)
            ?.date;

        return [
            // Building
            {
                id: 'building',
                content: (
                    <Chip
                        className="gap-1"
                        startContent={<Building weight="duotone" size={14} />}
                        classNames={{
                            base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                            content: "px-0 font-medium"
                        }}
                    >
                        {contract?.unit?.building?.id}
                    </Chip>
                )
            },
            // Unit
            {
                id: 'unit',
                content: (
                    <Chip
                        className="gap-1"
                        startContent={<Stack weight="duotone" size={14} />}
                        classNames={{
                            base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                            content: "px-0 font-medium"
                        }}
                    >
                        {`${contract?.unit?.id} ${contract?.unit?.referencePlus ? `(${contract?.unit?.referencePlus})` : ''}`}
                    </Chip>
                )
            },
            // Company
            {
                id: 'company',
                content: contract?.unit?.building?.company?.name ? (
                    <Chip
                        className="gap-1"
                        startContent={<Bank weight="duotone" size={14} />}
                        classNames={{
                            base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                            content: "px-0 font-medium"
                        }}
                    >
                        {contract.unit.building.company.name}
                    </Chip>
                ) : (
                    <span className="text-neutral-secondary-mid text-sm">—</span>
                )
            },
            // Tenants
            {
                id: 'tenant',
                content: tenants?.map((tenant) => (
                    <Chip
                        key={tenant?.id}
                        className="gap-1"
                        startContent={<UsersThree weight="duotone" size={18} />}
                        classNames={{
                            base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                            content: "px-0 font-medium text-clip overflow-hidden flex"
                        }}
                    >
                        <Link to={`/contact/${tenant?.resourceId}`} className="text-moss-primary">
                            {tenant?.name}
                        </Link>
                        <ArrowUpRight className="h-4 w-4 ml-1" />
                    </Chip>
                ))
            },
            // Reason
            {
                id: 'reason',
                content: contract.flaggedForWatchlistReason
            },
            // Start Date
            {
                id: 'startDate',
                content: startDate ? formatDate(new Date(startDate)) : ''
            },
            // Openstand (only when an incasso dossier exists for the unit)
            {
                id: 'openstand',
                content: typeof contract.incassoOpenstand === 'number' && contract.incassoOpenstand > 0 ? (
                    <Tooltip content="Openstaand bedrag — open dossier in incasso">
                        <Link
                            to={(() => {
                                const params = new URLSearchParams();
                                const buildingId = contract.unit?.building?.id;
                                const unitOdooId = contract.unit?.odooId;
                                if (buildingId) params.set('buildingNames', buildingId);
                                if (unitOdooId) params.set('openUnitOdooId', unitOdooId);
                                const qs = params.toString();
                                return `/rental/incasso/kanban${qs ? `?${qs}` : ''}`;
                            })()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex"
                        >
                            <Chip
                                className="gap-1"
                                startContent={<CurrencyEur weight="duotone" size={14} />}
                                classNames={{
                                    base: "h-fit bg-white px-2 border border-red-700 text-red-700 cursor-pointer hover:bg-red-50",
                                    content: "px-0 font-semibold"
                                }}
                            >
                                {formatEuro(contract.incassoOpenstand)}
                            </Chip>
                        </Link>
                    </Tooltip>
                ) : (
                    <span className="text-neutral-secondary-mid text-sm">—</span>
                )
            },
            // Actions
            {
                id: 'actions',
                content: (
                    <div className="flex cursor-pointer items-center gap-3">
                        <Tooltip content="Bewerk reden op watchlist">
                            <Pencil
                                weight="duotone"
                                size={22}
                                onClick={() => handleOpenEditWatchListReason(contract)}
                            />
                        </Tooltip>
                        <Tooltip content="Van watchlist verwijderen">
                            <Flag
                                weight="duotone"
                                size={22}
                                onClick={() => handleUnflagFlagFromWatchList(contract)}
                            />
                        </Tooltip>
                    </div>
                )
            }
        ];
    }, [handleOpenEditWatchListReason, handleUnflagFlagFromWatchList]);

    return (
        <div id="contracts-watchlist" className="col-span-full flex flex-col gap-2">
            {isLoading ? (
                <div className="flex h-full items-center justify-center p-8 rounded-lg bg-white shadow-sm">
                    <Spinner
                        label="Gegevens aan het ophalen"
                        color="success"
                        size="md"
                    />
                </div>
            ) : (
                <>
                    <UniversalTableSearch
                        rows={contracts}
                        searchValue={searchText}
                        onSearchChange={setSearchText}
                        filterSections={filterSections}
                        activeFilters={{
                            building: buildingFilter,
                            unit: unitFilter
                        }}
                        onFilterChange={(key, values) => {
                            if (key === 'building') setBuildingFilter(values);
                            if (key === 'unit') setUnitFilter(values);
                        }}
                        groupByOptions={groupByOptions}
                        selectedGroupBy={groupBy}
                        onGroupByChange={setGroupBy}
                    />

                    <UniversalTable
                        rows={filteredContracts}
                        title="Contracten op watchlist"
                        columnsConfig={{ columns: WATCHLIST_COLUMNS }}
                        groupBy={groupBy}
                        renderRow={renderRow}
                    />
                </>
            )}
            <FlagForWatchListModal
                incomingState={WatchListModalState.UNFLAG}
                incomingContract={selectedWatchlistContract}
                handleContractRefresh={() =>
                    softUnflagFromWatchList(selectedWatchlistContract?.id)
                }
                isOpen={isFlagForWatchListModalOpen}
                onClose={handleCloseFlagForWatchListModal}
            />
            <EditWatchListModal
                isOpen={editWatchlitReasonModalOpen}
                onClose={handleClose}
                incomingContract={selectedContract}
                handleContractRefresh={fetchWatchListContracts}
            />
        </div>
    );
};

export default WatchListSection;
